import { CATEGORY_SWATCHES } from '@insolitum/ui/theme/charts';
import {
  FolderTree,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Palette,
  Hash,
  ArrowUpDown,
  Tag,
  Layers,
  GripVertical,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// ── Types ───────────────────────────────────────────────────────────────────

interface Category {
  id: string;
  organization_id: string;
  name: string;
  slug: string;
  module_type: string;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CategoryFormData {
  name: string;
  slug: string;
  module_type: string;
  color: string;
  icon: string;
  sort_order: number;
}

// ── Constants ───────────────────────────────────────────────────────────────

const MODULE_TYPES = [
  { value: 'products', label: 'Produkty', color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
  { value: 'equipment', label: 'Sprzet', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  { value: 'documents', label: 'Dokumenty', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { value: 'suppliers', label: 'Dostawcy', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  { value: 'inventory', label: 'Magazyn', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { value: 'agenda', label: 'Agenda', color: 'text-pink-400', bg: 'bg-pink-500/20' },
  { value: 'costs', label: 'Koszty', color: 'text-red-400', bg: 'bg-red-500/20' },
  { value: 'global', label: 'Globalne', color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
];

// Te same swatche, co w kategoriach Agendy — jedno źródło w
// packages/ui/src/theme/charts.ts, bo to jedno i to samo pole w produkcie.
const PRESET_COLORS = CATEGORY_SWATCHES;

const initialFormData: CategoryFormData = {
  name: '',
  slug: '',
  module_type: 'products',
  color: '#3b82f6',
  icon: '',
  sort_order: 0,
};

// ── Helpers ─────────────────────────────────────────────────────────────────

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

// ── Component ───────────────────────────────────────────────────────────────

export function AdminCategoriesPage() {
  const { currentOrganization, hasRole } = useAuth();

  // Data
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterModuleType, setFilterModuleType] = useState<string>('all');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<CategoryFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [autoSlug, setAutoSlug] = useState(true);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canManage = hasRole(['owner', 'admin', 'manager']);

  // ── Fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentOrganization) fetchCategories();
  }, [currentOrganization]);

  const fetchCategories = async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('categories')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .order('module_type')
        .order('sort_order')
        .order('name');
      if (err) throw err;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Nie udalo sie zaladowac kategorii');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const filteredCategories = useMemo(() => {
    return categories.filter((c) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.slug.toLowerCase().includes(q) ||
        c.module_type.toLowerCase().includes(q);
      const matchesModule = filterModuleType === 'all' || c.module_type === filterModuleType;
      return matchesSearch && matchesModule;
    });
  }, [categories, searchQuery, filterModuleType]);

  const getModuleTypeInfo = (type: string) =>
    MODULE_TYPES.find((t) => t.value === type) || MODULE_TYPES[0];

  // ── Handlers ──────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingCategory(null);
    const nextSortOrder =
      categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order)) + 1 : 0;
    setFormData({ ...initialFormData, sort_order: nextSortOrder });
    setFormError(null);
    setAutoSlug(true);
    setShowModal(true);
  };

  const openEditModal = (category: Category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      module_type: category.module_type,
      color: category.color || '#3b82f6',
      icon: category.icon || '',
      sort_order: category.sort_order,
    });
    setFormError(null);
    setAutoSlug(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData(initialFormData);
    setFormError(null);
    setAutoSlug(true);
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      slug: autoSlug ? generateSlug(name) : prev.slug,
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    if (!formData.name.trim()) {
      setFormError('Nazwa kategorii jest wymagana');
      return;
    }
    if (!formData.slug.trim()) {
      setFormError('Slug jest wymagany');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const payload = {
        organization_id: currentOrganization.organization_id,
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        module_type: formData.module_type,
        color: formData.color || null,
        icon: formData.icon.trim() || null,
        sort_order: formData.sort_order,
      };

      if (editingCategory) {
        const { error: err } = await supabase
          .from('categories')
          .update(payload)
          .eq('id', editingCategory.id);
        if (err) throw err;
        setSuccess('Kategoria zostala zaktualizowana');
      } else {
        const { error: err } = await supabase
          .from('categories')
          .insert([{ ...payload, is_active: true }]);
        if (err) {
          if (err.code === '23505') {
            setFormError('Kategoria o tym slugu juz istnieje');
            setIsSaving(false);
            return;
          }
          throw err;
        }
        setSuccess('Kategoria zostala dodana');
      }

      await fetchCategories();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving category:', err);
      setFormError('Nie udalo sie zapisac kategorii');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      const { error: err } = await supabase.from('categories').delete().eq('id', id);
      if (err) throw err;
      await fetchCategories();
      setDeleteTarget(null);
      setSuccess('Kategoria zostala usunieta');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Nie udalo sie usunac kategorii. Moze byc uzywana przez inne rekordy.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-56 skel" />
            <div className="h-4 w-40 skel mt-2" />
          </div>
          <div className="h-10 w-44 skel" />
        </div>
        <div className="h-10 skel" />
        <div className="card tablecard">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-aurora-border">
              <div className="h-6 w-6 skel" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-32 skel" />
                <div className="h-3 w-20 skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <FolderTree className="w-6 h-6 text-purple-400" />
            Kategorie
          </h1>
          <p className="pagehead__sub">
            Zarzadzaj kategoriami organizacji (<span className="mono">{categories.length}</span>)
          </p>
        </div>
        {canManage && (
          <div className="pagehead__actions">
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj kategorie
            </button>
          </div>
        )}
      </div>

      {/* Module type stat pills */}
      <div className="flex flex-wrap gap-2">
        {MODULE_TYPES.map((mt) => {
          const count = categories.filter((c) => c.module_type === mt.value).length;
          return (
            <button
              key={mt.value}
              onClick={() => setFilterModuleType(filterModuleType === mt.value ? 'all' : mt.value)}
              aria-pressed={filterModuleType === mt.value}
              className="chip"
            >
              <Layers className="w-3.5 h-3.5" />
              {mt.label}
              <span className={`badge ${filterModuleType === mt.value ? 'badge--brand' : ''}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-ids-lg text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search */}
      <div className="inputwrap">
        <Search />
        <input
          type="text"
          placeholder="Szukaj po nazwie, slugu, typie modulu..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="input"
        />
      </div>

      {/* Table */}
      {filteredCategories.length === 0 ? (
        <div className="card p-12 text-center">
          <FolderTree className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterModuleType !== 'all'
              ? 'Nie znaleziono kategorii'
              : 'Brak kategorii'}
          </h3>
          <p className="text-aurora-muted mb-4">
            {searchQuery ? 'Sprobuj zmienic kryteria wyszukiwania' : 'Dodaj pierwsza kategorie'}
          </p>
          {!searchQuery && canManage && (
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj kategorie
            </button>
          )}
        </div>
      ) : (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Typ modulu</th>
                  <th>Kolor</th>
                  <th>Ikona</th>
                  <th>
                    <div className="flex items-center gap-1">
                      <ArrowUpDown className="w-3.5 h-3.5" />
                      Kolejnosc
                    </div>
                  </th>
                  {canManage && <th className="text-right">Akcje</th>}
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((category) => {
                  const moduleInfo = getModuleTypeInfo(category.module_type);
                  return (
                    <tr key={category.id}>
                      <td>
                        <div className="cellname">
                          {category.color && (
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-aurora-border"
                              style={{ backgroundColor: category.color }}
                            />
                          )}
                          <div>
                            <p className="text-sm">{category.name}</p>
                            <p className="text-xs font-normal text-aurora-subtle mono">
                              {category.slug}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${moduleInfo.bg} ${moduleInfo.color}`}>
                          <Layers className="w-3 h-3" />
                          {moduleInfo.label}
                        </span>
                      </td>
                      <td>
                        {category.color ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded border border-aurora-border-strong"
                              style={{ backgroundColor: category.color }}
                            />
                            <span className="text-xs text-aurora-muted mono">{category.color}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-aurora-subtle">-</span>
                        )}
                      </td>
                      <td className="text-sm text-aurora-muted">{category.icon || '-'}</td>
                      <td>
                        <span className="inline-flex items-center gap-1 text-sm text-aurora-muted mono">
                          <GripVertical className="w-3.5 h-3.5" />
                          {category.sort_order}
                        </span>
                      </td>
                      {canManage && (
                        <td>
                          <div className="flex items-center justify-end gap-1 rowact">
                            <button
                              onClick={() => openEditModal(category)}
                              className="p-1.5 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors"
                              title="Edytuj"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(category.id)}
                              className="p-1.5 text-aurora-muted hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                              title="Usun"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full">
            <div className="modal__head">
              <h3 className="modal__title">Potwierdzenie usuwania</h3>
            </div>
            <div className="modal__body">
              <p className="text-sm text-aurora-muted">
                Czy na pewno chcesz usunac te kategorie? Upewnij sie, ze nie jest uzywana przez inne
                rekordy.
              </p>
            </div>
            <div className="modal__foot">
              <button onClick={() => setDeleteTarget(null)} className="btn btn--ghost">
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={isDeleting}
                className="btn btn--danger"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Usun
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="modal__head">
              <div className="w-10 h-10 bg-purple-500/15 rounded-ids-md flex items-center justify-center flex-shrink-0">
                <FolderTree className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="modal__title">
                  {editingCategory ? 'Edytuj kategorie' : 'Dodaj kategorie'}
                </h2>
                <p className="text-sm text-aurora-muted truncate">
                  {editingCategory ? editingCategory.name : 'Wypelnij dane kategorii'}
                </p>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form id="category-form" onSubmit={handleSave} className="space-y-5">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                <div className="field">
                  <label className="field__label">
                    Nazwa <span className="text-aurora-brand">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    required
                    placeholder="np. Pieczywo, Sprzet kuchenny"
                    className="input"
                  />
                </div>

                <div className="field">
                  <label className="field__label">
                    <div className="flex items-center gap-1.5">
                      <Hash className="w-4 h-4" />
                      Slug <span className="text-aurora-brand">*</span>
                    </div>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => {
                        setAutoSlug(false);
                        setFormData({ ...formData, slug: e.target.value });
                      }}
                      required
                      placeholder="auto-generowany-z-nazwy"
                      className="input mono flex-1 text-sm"
                    />
                    {!autoSlug && (
                      <button
                        type="button"
                        onClick={() => {
                          setAutoSlug(true);
                          setFormData((prev) => ({ ...prev, slug: generateSlug(prev.name) }));
                        }}
                        className="btn btn--secondary btn--sm self-center whitespace-nowrap"
                      >
                        Auto
                      </button>
                    )}
                  </div>
                  <p className="field__hint">
                    Identyfikator URL-friendly (generowany automatycznie z nazwy)
                  </p>
                </div>

                <div className="field">
                  <label className="field__label">
                    <div className="flex items-center gap-1.5">
                      <Layers className="w-4 h-4" />
                      Typ modulu <span className="text-aurora-brand">*</span>
                    </div>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {MODULE_TYPES.map((mt) => (
                      <button
                        key={mt.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, module_type: mt.value })}
                        aria-pressed={formData.module_type === mt.value}
                        className={`flex items-center gap-2 p-2.5 rounded-ids-md border transition-all text-left text-sm ${
                          formData.module_type === mt.value
                            ? 'border-aurora-brand bg-aurora-brand/10 shadow-glow'
                            : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                        }`}
                      >
                        <div className={`p-1 rounded ${mt.bg}`}>
                          <Layers className={`w-3.5 h-3.5 ${mt.color}`} />
                        </div>
                        <span className="text-aurora-text font-medium">{mt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label className="field__label">
                    <div className="flex items-center gap-1.5">
                      <Palette className="w-4 h-4" />
                      Kolor
                    </div>
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setFormData({ ...formData, color })}
                          className={`w-7 h-7 rounded-ids-md border-2 transition-all ${
                            formData.color === color
                              ? 'border-aurora-text scale-110'
                              : 'border-transparent hover:border-aurora-border-strong'
                          }`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-10 h-10 rounded-ids-md border border-aurora-border-strong bg-transparent cursor-pointer"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="field">
                    <label className="field__label">
                      <div className="flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        Ikona
                      </div>
                    </label>
                    <input
                      type="text"
                      value={formData.icon}
                      onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                      placeholder="np. package, truck, file"
                      className="input"
                    />
                    <p className="field__hint">Nazwa ikony Lucide</p>
                  </div>
                  <div className="field">
                    <label className="field__label">
                      <div className="flex items-center gap-1.5">
                        <ArrowUpDown className="w-4 h-4" />
                        Kolejnosc sortowania
                      </div>
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.sort_order}
                      onChange={(e) =>
                        setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })
                      }
                      className="input mono"
                    />
                  </div>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-aurora-brand">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="category-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingCategory ? 'Zapisz zmiany' : 'Dodaj kategorie'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCategoriesPage;
